import { faker } from '@faker-js/faker';
import { fakeVersion, generateInitialPaginationApi } from '@ingenico/api-sdk-core/mocks';
import type {
  BffSystemPackageVersionResourceReadV2Api,
  DownloadPolicyApi,
  GetAllSystemPackageVersionArtifactsBySystemPackageVersionIdV2QueryResponseApi,
  PackageRefApi,
  PaginationApi,
  SearchRequestApi,
  SearchSystemPackageVersionsV2MutationResponseApi,
  SoftwareVersionResourceArtifactItemV2Api,
  SystemPackageVersionResourceListResponseV2Api,
  SystemPackageVersionResourceReadV2Api,
  TemplateResourceListResponseV2Api,
} from '@ingenico/api-sdk-device-management-private-v2';
import { enumVersionPublicationStatus } from '@ingenico/api-sdk-device-management-private-v2';
import { toNumber } from 'lodash-es';
import type { PathParams, HttpRequestResolverExtras, ResponseResolverInfo } from 'msw';
import { HttpResponse } from 'msw';
import { generateArtifactReadV2Api } from './common';
import { initialGetApplicationVersionTemplatesByApplicationIdCustomHandler } from './templates-versions';

export function searchSystemApplicationVersionCustomHandler(
  context: ResponseResolverInfo<HttpRequestResolverExtras<PathParams>>,
) {
  const url = new URL(context.request.url);

  const offsetParam = url.searchParams.get('offset') ?? 0;
  const limitParam = url.searchParams.get('limit') ?? 10;

  const offset = toNumber(offsetParam);
  const limit = toNumber(limitParam);

  return HttpResponse.json<SystemPackageVersionResourceListResponseV2Api>(
    searchSystemPackageVersion({ limit, offset }),
  );
}

export function getSystemPackagesVersionTemplatesById(): TemplateResourceListResponseV2Api {
  return {
    pagination: {
      itemsPerPage: 10,
      startIndex: 0,
      totalResults: 1,
    },
    data: [
      {
        componentType: 'APPLICATION',
        createdBy: faker.string.uuid(),
        creationDate: faker.date.past(),
        description: faker.lorem.sentence(),
        id: faker.string.uuid(),
        name: faker.lorem.words(3),
        targetLocation: faker.system.filePath(),
        templateFileId: faker.string.uuid(),
        templateFileName: faker.lorem.words(3),
        templateFileVersion: faker.system.semver(),
        checksum: faker.lorem.word(),
        extension: faker.system.fileExt(),
        fileName: faker.system.fileName(),
        mimeType: faker.system.mimeType(),
        size: faker.number.int(),
        type: faker.helpers.arrayElement(['SW_PACKAGE', 'TEMPLATE']),
      },
    ],
  };
}
export function getBffSystemPackageVersionByIdV2ResponseMock(): BffSystemPackageVersionResourceReadV2Api {
  return { ...generateSystemPackageVersionReadV2Api(faker.string.uuid()), compatibleOsVersions: [] };
}

const mockedArtifacts = Array(faker.number.int({ min: 10, max: 100 }))
  .fill(null)
  .map(() => generateArtifactReadV2Api());

function generateDownloadPolicies(artifacts?: SoftwareVersionResourceArtifactItemV2Api[]): DownloadPolicyApi[] {
  return artifacts
    ?.map(({ id: artifactId }) => {
      const shouldGenerate = faker.helpers.arrayElement([true, false]);
      if (shouldGenerate) {
        const actionsCount = faker.number.int({ min: 1, max: 3 });

        return {
          artifactId,
          actions: [
            {
              deletePackages: Array(actionsCount)
                .fill(undefined)
                .map(
                  () =>
                    ({
                      name: faker.lorem.word(),
                      version: fakeVersion(),
                    }) satisfies PackageRefApi,
                ),
            },
          ],
        } satisfies DownloadPolicyApi;
      } else {
        return null;
      }
    })
    .filter(Boolean) as DownloadPolicyApi[];
}

export function generateSystemPackageVersionReadV2Api(systemPackageId: string): SystemPackageVersionResourceReadV2Api {
  const versionNumber = fakeVersion();
  const creationDate = faker.date.past();
  return {
    id: faker.string.uuid(),
    downloadPolicies: generateDownloadPolicies(mockedArtifacts),
    systemPackageId,
    versionNumber: faker.system.semver(),
    shortDescription: faker.lorem.paragraph(),
    status: faker.helpers.arrayElement(Object.values(enumVersionPublicationStatus)),
    artifacts: [
      ...faker.helpers.multiple(() => ({
        id: faker.string.uuid(),
        order: faker.number.int(),
      })),
    ],
    compatibleOsVersionIds: faker.helpers.multiple(faker.string.uuid),
    creationDate,
    longDescription:
      `**Version ${versionNumber} - ${creationDate.toISOString()}**\n` +
      '\n' +
      '- New Features:\n' +
      '  - Introduced support for NFC payments for a seamless tap-and-go experience.\n' +
      '  - Added multi-language support with 5 new languages: Spanish, French, German, Italian, and Japanese.\n' +
      '  - Enhanced dashboard with real-time transaction analytics.\n' +
      '\n' +
      '- Improvements:\n' +
      '  - Optimized transaction processing speed, reducing average time by 30%.\n' +
      '  - Improved UI for easier navigation and better accessibility.\n' +
      '  - Upgraded security protocols to comply with the latest PCI DSS standards.\n' +
      '\n' +
      '- Bug Fixes:\n' +
      '  - Fixed issue causing occasional crashes during batch settlement.\n' +
      '  - Resolved display errors in the transaction history.\n' +
      '  - Corrected alignment issues in printed receipts.',
    publisherId: faker.string.uuid(),
  };
}

export async function searchSystemPackageVersionsV2CustomHandler(
  context: ResponseResolverInfo<HttpRequestResolverExtras<PathParams>>,
) {
  const body = (await context.request.json()) as SearchRequestApi;
  const count = faker.number.int({ min: 5, max: 25 });

  const systemPackageId: string = body.filters.length > 0 ? body.filters[0].value : faker.string.uuid();

  const response: SearchSystemPackageVersionsV2MutationResponseApi = {
    pagination: generateInitialPaginationApi(count),
    data: faker.helpers.multiple(() => generateSystemPackageVersionReadV2Api(systemPackageId), { count }),
  };

  return HttpResponse.json<SearchSystemPackageVersionsV2MutationResponseApi>(response);
}

export function getAllSystemPackageVersionArtifactsBySystemPackageVersionIdV2ResponseMock(): GetAllSystemPackageVersionArtifactsBySystemPackageVersionIdV2QueryResponseApi {
  return {
    data: mockedArtifacts,
    pagination: generateInitialPaginationApi(mockedArtifacts.length),
  };
}

const qttLines = faker.number.int({ min: 10, max: 30 });

function searchSystemPackageVersion({
  offset,
  limit,
}: {
  readonly offset: number;
  readonly limit: number;
}): SystemPackageVersionResourceListResponseV2Api {
  return {
    data: faker.helpers.multiple(generateSystemPackageVersion, { count: limit }),
    pagination: generatePaginationApi({ total: qttLines, offset, limit }),
  };
}

function generatePaginationApi({
  total,
  offset,
  limit,
}: {
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
}): PaginationApi {
  const initialTerm = Math.ceil(offset / total);
  return {
    totalResults: total,
    startIndex: initialTerm,
    itemsPerPage: limit,
  };
}

function generateSystemPackageVersion(): SystemPackageVersionResourceReadV2Api {
  return {
    systemPackageId: faker.string.uuid(),
    id: faker.string.uuid(),
    status: 'PUBLISHED',
    templates: generateTemplates(),
    artifacts: generateArtifacts(),
    publisherId: faker.string.uuid(),
    versionNumber: fakeVersion(),
    creationDate: faker.date.past(),
  };
}

function generateArtifacts(): SystemPackageVersionResourceReadV2Api['artifacts'] {
  const artifactsCount = faker.number.int({ min: 1, max: 4 });

  return Array(artifactsCount)
    .fill(undefined)
    .map((_, index) => ({
      id: faker.string.uuid(),
      order: index,
    })) satisfies SystemPackageVersionResourceReadV2Api['artifacts'];
}

function generateTemplates(): SystemPackageVersionResourceReadV2Api['templates'] {
  const artifactsCount = faker.number.int({ min: 1, max: 3 });

  return Array(artifactsCount)
    .fill(undefined)
    .map(() => ({
      artifactId: faker.string.uuid(),
    })) satisfies SystemPackageVersionResourceReadV2Api['templates'];
}

export function initialGetSystemPackageVersionTemplatesByApplicationIdCustomHandler(): TemplateResourceListResponseV2Api {
  return initialGetApplicationVersionTemplatesByApplicationIdCustomHandler();
}

export function getSystemPackageVersionTemplatesByApplicationIdCustomHandler() {
  return HttpResponse.json<TemplateResourceListResponseV2Api>({
    pagination: {
      itemsPerPage: 10,
      startIndex: 0,
      totalResults: 2,
    },
    data: [
      {
        type: 'TEMPLATE',
        checksum: '1e234c5b-cf9a-4c98-915f-cb96085a5821',
        mimeType: 'application/vnd.apple.installer+xml',
        size: 4736,
        fileName: 'lively.oga',
        id: 'd0e8e8e0-1b4d-4336-99b8-548e08b05777',
        name: 'sortitus',
        creationDate: new Date('2024-12-17T17:37:28.477Z'),
        componentType: 'OTHER',
        createdBy: 'baf4f219-87f1-4bb7-af08-6fe788951602',
        extension: 'xml',
        description: 'Id advenio suus comedo copiose consectetur temeritas bene speculum.',
        targetLocation: '/var/log/eek.otf',
        templateFileId: '23fccac9-0682-469f-9e01-6b3b437a86f2',
        templateFileName: 'sans_truly_yet.jar',
        templateFileVersion: '1.2.4',
      },
      {
        type: 'SW_PACKAGE',
        checksum: '01640332-1805-41f2-8f09-8c3f1797d202',
        mimeType: 'application/x-sh',
        size: 8628,
        fileName: 'around_indeed_patiently.ttf',
        id: 'd0e8e8e0-1b4d-4336-99b8-548e08b05666',
        name: 'utrum',
        creationDate: new Date('2024-09-30T03:53:45.152Z'),
        componentType: 'OTHER',
        createdBy: '2cf00cb6-6c86-4da6-bdc4-c04eb629db7b',
        extension: 'ttf',
        description: 'Cohaero quam aestas vestrum admoneo iusto.',
        targetLocation: '/usr/local/src/mammoth_suckle.gif',
        templateFileId: 'bec8649d-17eb-4fb2-8a7b-40758117beb2',
        templateFileName: 'classic_unless.ifb',
        templateFileVersion: '1.2.3',
      },
    ],
  });
}
